import React, { useMemo } from "react";
import { Lock } from "@mui/icons-material";
import { Box } from "@mui/material";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useGetImportLayers } from "fond/hooks/useGetImportLayers";
import { ImportStatus, isImportInProgress, NBNIDDLiteInputLayerIds, Project, WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AccordionStepper, AccordionStepperPane, NonIdealState } from "fond/widgets";

import { DataPrepPanel } from "../InputPanel";
import InputPanel from "../InputPanel/InputPanel";

import GeneratePanel from "./GeneratePanel";

interface IDDLiteDesignPanelProps {
  // The project currently being viewed / edited
  project: Project;

  // Flag indicating if the project is read only
  readOnly: boolean;
}

const IDDLiteDesignPanel: React.FC<IDDLiteDesignPanelProps> = ({ project, readOnly }: IDDLiteDesignPanelProps) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const inputLayers = useGetImportLayers(versionId, Object.values(NBNIDDLiteInputLayerIds));
  const { workflowState: cleanWorkflow } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.NBN_IDD_LITE.CLEAN_DATA });
  const { workflowState: generateWorkflow } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.NBN_IDD_LITE.GENERATE_DESIGN });

  const hasInProgressLayers = useMemo(() => {
    return Array.from(inputLayers.values()).some((layer) => layer.status && isImportInProgress(layer.status));
  }, [inputLayers]);

  const errorLayers = useMemo(() => {
    return Array.from(inputLayers.values()).filter((layer) => layer.status === ImportStatus.ERROR);
  }, [inputLayers]);

  const allLayersUploaded = useMemo(() => {
    return Array.from(inputLayers.values()).some((layer) => layer.layer);
  }, [inputLayers]);

  const isImportComplete = allLayersUploaded && !hasInProgressLayers && errorLayers.length === 0;
  const isCleanInProgress = cleanWorkflow === "STARTING" || cleanWorkflow === "IN_PROGRESS";
  const isGenerateInProgress = generateWorkflow === "STARTING" || generateWorkflow === "IN_PROGRESS";

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Input data",
        id: "input",
        content: (
          <InputPanel inProgress={hasInProgressLayers} errors={errorLayers} complete={isImportComplete} isReadOnly={readOnly} type="nbn_idd_lite" />
        ),
        isComplete: allLayersUploaded && !hasInProgressLayers && errorLayers.length === 0,
        isLoading: hasInProgressLayers,
      },
      {
        header: "Data preparation",
        id: "dataPrep",
        content: <DataPrepPanel isReadOnly={readOnly} />,
        isComplete: cleanWorkflow === "COMPLETE",
        isLoading: isCleanInProgress,
      },
      {
        header: "Design",
        id: "design",
        content: <GeneratePanel />,
        isComplete: generateWorkflow === "COMPLETE",
        isLoading: isGenerateInProgress,
      },
    ],
    [
      allLayersUploaded,
      cleanWorkflow,
      errorLayers,
      generateWorkflow,
      hasInProgressLayers,
      isCleanInProgress,
      isGenerateInProgress,
      isImportComplete,
      readOnly,
    ]
  );

  if (!project) return null;

  return (
    <Box data-testid="nbn-cost-to-serve-design-panel" sx={{ height: "100%" }}>
      {!permissionCheck(project.Permission?.Level, Actions.PROJECT_EDIT) ? ( // if user has access and no license, more granular "locks" are applied per component
        <NonIdealState size="small" icon={<Lock />} title="View only access" description="You only have view access to this project" />
      ) : (
        <AccordionStepper panes={accordionPanes} id={project.ID} />
      )}
    </Box>
  );
};

export default IDDLiteDesignPanel;
